export const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "svg",
  "avif",
];

// Formats a browser cannot render on its own. Callers fall back to downloading
// or opening these in a new tab instead of showing an empty preview.
export const NON_PREVIEWABLE_EXTENSIONS = [
  "xlsx",
  "xls",
  "csv",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "zip",
  "rar",
  "7z",
];

export const MIME_EXTENSIONS = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

export const getExtension = (url = "") => {
  const fileName = String(url).split("?")[0].split("#")[0].split("/").pop() || "";
  if (!fileName.includes(".")) return "";
  return fileName.split(".").pop().toLowerCase();
};

/**
 * Stored file names are inconsistent: most uploads are .jpg/.png, a large batch
 * was written as ".octet-stream", and a few carry no extension at all. Those
 * unnamed files are all images, so anything unrecognised is treated as one and
 * only falls back once the browser refuses to decode it.
 */
export const getFileKind = (url = "") => {
  const extension = getExtension(url);
  if (extension === "pdf") return "pdf";
  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (NON_PREVIEWABLE_EXTENSIONS.includes(extension)) return "other";
  return "unknown";
};

/**
 * Whether `openFilePreview` should show a file inline rather than open a tab.
 *
 * Deliberately conservative: only files we can positively identify as a PDF or
 * an image preview automatically. Anything else — export endpoints, blob URLs,
 * extensionless links — keeps the previous new-tab behaviour unless the caller
 * states the kind explicitly.
 */
export const isPreviewable = (url = "", kind) => {
  if (!url) return false;
  const resolved = kind || getFileKind(url);
  return resolved === "pdf" || resolved === "image";
};

export const buildFileName = (url, { label = "document", suffix = "", mimeType } = {}) => {
  const urlExtension = getExtension(url);
  const extension =
    (urlExtension === "pdf" || IMAGE_EXTENSIONS.includes(urlExtension)
      ? urlExtension
      : MIME_EXTENSIONS[mimeType]) || "file";
  const parts = [label, suffix]
    .filter(Boolean)
    .map((part) => String(part).trim().replace(/\s+/g, "_").toLowerCase());
  const date = new Date().toISOString().split("T")[0];
  return `${parts.join("_")}_${date}.${extension}`;
};

/**
 * Downloads through a blob so the admin panel stays on screen and the file gets
 * a meaningful name — the `download` attribute is ignored on cross-origin URLs,
 * so a plain link would navigate away instead of saving.
 */
export const downloadFile = async (url, fileNameOrOptions) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const fileName =
    typeof fileNameOrOptions === "string"
      ? fileNameOrOptions
      : buildFileName(url, { ...fileNameOrOptions, mimeType: blob.type });

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};
